import { memo, useEffect, useState } from 'react';

import toastManager from 'utils/ToastManager';

import Alert from './Alert';
import * as S from './styled/ToastManager.styled';

/**
 * Individual Toast Component
 * Handles animation and lifecycle of a single toast
 *
 * @author @buunsal
 */
const Toast = memo(({ data, onRemove }) => {
	const { id, type, header, message, link, linkAction, persist, onClose, duration } = data;

	const [showing, setShowing] = useState(true);
	const [exiting, setExiting] = useState(false);

	const closeAlert = () => {
		onRemove(id);
		onClose && onClose();
	};

	const handleCloseAction = () => {
		setExiting(true);
		setTimeout(() => {
			closeAlert();
		}, 600);
	};

	const handleLinkAction = () => {
		linkAction && linkAction();
		handleCloseAction();
	};

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowing(false);
		}, 600);

		if (link !== undefined || persist) {
			return;
		}

		const timer2 = setTimeout(() => {
			handleCloseAction();
		}, duration);

		return () => {
			timer && clearTimeout(timer);
			timer2 && clearTimeout(timer2);
		};
	}, []);

	return (
		<S.ToastWrapper
			className={`${showing ? 'showing' : ''} ${exiting ? 'exiting' : ''}`}
			onMouseDown={(event) => event.stopPropagation()}
			onTouchEnd={(event) => event.stopPropagation()}>
			<Alert
				variant={type}
				compact={false}
				showIcon
				header={header}
				content={message}
				ctaButtonLabel={link}
				onCtaButtonClick={handleLinkAction}
				onClose={handleCloseAction}
			/>
		</S.ToastWrapper>
	);
});

Toast.displayName = 'Toast';

/**
 * Toast Manager Component
 * Renders and manages all active toasts
 *
 * @author @buunsal
 */
const ToastManager = () => {
	const [visibleToasts, setVisibleToasts] = useState([]);

	useEffect(() => {
		// Initialize with current toasts
		setVisibleToasts(toastManager.getVisibleToasts());

		// Subscribe to changes
		const unsubscribe = toastManager.subscribe(() => {
			setVisibleToasts(toastManager.getVisibleToasts());
		});

		// Cleanup on unmount
		return unsubscribe;
	}, []);

	return (
		<S.ToastManagerContainer>
			{visibleToasts.map((data) => (
				<Toast key={data.id} data={data} onRemove={toastManager.remove.bind(toastManager)} />
			))}
		</S.ToastManagerContainer>
	);
};

export default ToastManager;
